export class NotificationService {
    static SETTINGS_NOTIFICATIONS = 'notifications';
    static SETTINGS_NOTIFICATION_PREVIEW = 'notificationPreview';
    static SETTINGS_NOTIFICATION_SOUND = 'notificationSound';
    static NOTIFICATION_SOUND = 'sounds/notification.mp3';
    $window;
    $state;
    settingsService;
    log;
    notificationPermission = null;
    desktopNotifications = null;
    notificationAPIAvailable = null;
    notificationPreview = null;
    notificationSound = null;
    notificationCache = {};
    static $inject = ['$window', '$state', 'LogService', 'SettingsService'];
    constructor($window, $state, logService, settingsService) {
        this.$window = $window;
        this.$state = $state;
        this.settingsService = settingsService;
        this.log = logService.getLogger('Notification-S');
    }
    init() {
        this.checkNotificationAPI();
        this.fetchSettings();
    }
    requestNotificationPermission() {
        if (this.notificationAPIAvailable) {
            const Notification = this.$window.Notification;
            this.log.debug('Requesting notification permission...');
            Notification.requestPermission((result) => {
                switch (result) {
                    case 'denied':
                        this.notificationPermission = false;
                        break;
                    case 'granted':
                        this.notificationPermission = true;
                        this.desktopNotifications = true;
                        this.storeSetting(NotificationService.SETTINGS_NOTIFICATIONS, 'true');
                        break;
                    case 'default':
                        this.desktopNotifications = false;
                        this.notificationPermission = null;
                        break;
                    default:
                        this.notificationPermission = false;
                        break;
                }
                this.log.debug('Notification permission', this.notificationPermission);
            });
        }
    }
    checkNotificationAPI() {
        this.notificationAPIAvailable = ('Notification' in this.$window);
        this.log.debug('Notification API available:', this.notificationAPIAvailable);
        if (this.notificationAPIAvailable) {
            const Notification = this.$window.Notification;
            switch (Notification.permission) {
                case 'denied':
                    this.notificationPermission = false;
                    break;
                case 'granted':
                    this.notificationPermission = true;
                    break;
                case 'default':
                    this.notificationPermission = null;
                    break;
                default:
                    this.notificationPermission = false;
                    break;
            }
        }
        this.log.debug('Initial notificationPermission', this.notificationPermission);
    }
    fetchSettings() {
        this.log.debug('Fetching settings...');
        const notifications = this.retrieveSetting(NotificationService.SETTINGS_NOTIFICATIONS);
        const preview = this.retrieveSetting(NotificationService.SETTINGS_NOTIFICATION_PREVIEW);
        const sound = this.retrieveSetting(NotificationService.SETTINGS_NOTIFICATION_SOUND);
        if (notifications === 'true') {
            this.log.debug('Desktop notifications:', notifications);
            this.desktopNotifications = true;
            this.requestNotificationPermission();
        }
        else if (notifications === 'false') {
            this.log.debug('Desktop notifications:', notifications);
            this.desktopNotifications = false;
        }
        else {
            this.log.debug('Desktop notifications:', notifications, 'Asking user...');
            this.requestNotificationPermission();
        }
        if (preview === 'false') {
            this.log.debug('Notification preview:', preview);
            this.notificationPreview = false;
        }
        else {
            this.log.debug('Notification preview:', preview, 'Using default value (true)');
            this.notificationPreview = true;
            this.storeSetting(NotificationService.SETTINGS_NOTIFICATION_PREVIEW, 'true');
        }
        if (sound === 'true') {
            this.log.debug('Notification sound:', sound);
            this.notificationSound = true;
        }
        else {
            this.log.debug('Notification sound:', sound, 'Using default value (false)');
            this.notificationSound = false;
            this.storeSetting(NotificationService.SETTINGS_NOTIFICATION_SOUND, 'false');
        }
    }
    getNotificationPermission() {
        return this.notificationPermission;
    }
    getWantsNotifications() {
        return this.desktopNotifications;
    }
    getWantsPreview() {
        return this.notificationPreview;
    }
    getWantsSound() {
        return this.notificationSound;
    }
    isNotificationApiAvailable() {
        return this.notificationAPIAvailable;
    }
    setWantsNotifications(wantsNotifications) {
        this.log.debug('Requesting notification preference change to', wantsNotifications);
        if (wantsNotifications) {
            this.requestNotificationPermission();
        }
        else {
            this.desktopNotifications = false;
            this.storeSetting(NotificationService.SETTINGS_NOTIFICATIONS, 'false');
        }
    }
    setWantsPreview(wantsPreview) {
        this.log.debug('Requesting preview preference change to', wantsPreview);
        this.notificationPreview = wantsPreview;
        this.storeSetting(NotificationService.SETTINGS_NOTIFICATION_PREVIEW, wantsPreview ? 'true' : 'false');
    }
    setWantsSound(wantsSound) {
        this.log.debug('Requesting sound preference change to', wantsSound);
        this.notificationSound = wantsSound;
        this.storeSetting(NotificationService.SETTINGS_NOTIFICATION_SOUND, wantsSound ? 'true' : 'false');
    }
    storeSetting(key, value) {
        this.settingsService.storeUntrustedKeyValuePair(key, value);
    }
    retrieveSetting(key) {
        return this.settingsService.retrieveUntrustedKeyValuePair(key);
    }
    getAppNotificationSettings(conversation) {
        if (!conversation.notifications) {
            return {
                sound: { muted: false },
                dnd: { enabled: false, mentionOnly: false },
            };
        }
        const sound = conversation.notifications.sound;
        const dnd = conversation.notifications.dnd;
        const simpleSound = {
            muted: !(sound.mode === "default"),
        };
        const simpleDnd = {
            enabled: dnd.mode === "on",
            mentionOnly: (dnd.mentionOnly === undefined || dnd.mentionOnly === null) ? false : dnd.mentionOnly,
        };
        if (dnd.mode === "until") {
            if (!dnd.until || dnd.until <= 0) {
                simpleDnd.enabled = false;
            }
            else {
                const until = new Date(dnd.until);
                const now = new Date();
                simpleDnd.enabled = until > now;
            }
        }
        if (!simpleDnd.enabled) {
            simpleDnd.mentionOnly = false;
        }
        return {
            sound: simpleSound,
            dnd: simpleDnd,
        };
    }
    getDndModeSimplified(conversation) {
        const simplified = this.getAppNotificationSettings(conversation);
        if (simplified.dnd.enabled) {
            return simplified.dnd.mentionOnly ? 'mention' : 'on';
        }
        return 'off';
    }
    showNotification(tag, title, body, avatar = '/img/threema-64x64.png', clickCallback, forceShowBody = false, overwriteOlder = false, forceMute = false) {
        if (this.notificationSound && !forceMute) {
            const audio = new Audio(NotificationService.NOTIFICATION_SOUND);
            audio.play();
        }
        if (this.notificationPermission !== true || this.desktopNotifications !== true) {
            return false;
        }
        if (!this.notificationPreview && !forceShowBody) {
            body = '';
            if (this.notificationCache[tag]) {
                this.clearCache(tag);
            }
        }
        if (overwriteOlder === true) {
            this.clearCache(tag);
        }
        if (this.notificationCache[tag]) {
            body = `${this.notificationCache[tag].body}\n${body}`;
        }
        this.log.debug('Showing notification', tag);
        const notification = new this.$window.Notification(title, {
            icon: avatar,
            body: body.trim(),
            tag: tag,
        });
        notification.onclick = () => {
            this.$window.focus();
            if (clickCallback !== undefined) {
                clickCallback();
            }
            this.log.debug('Hiding notification', tag, 'on click');
            notification.close();
            this.clearCache(tag);
        };
        this.notificationCache[tag] = notification;
        return true;
    }
    hideNotification(tag) {
        const notification = this.notificationCache[tag];
        if (notification !== undefined) {
            this.log.debug('Hiding notification', tag);
            notification.close();
            this.clearCache(tag);
            return true;
        }
        else {
            return false;
        }
    }
    clearCache(tag) {
        delete this.notificationCache[tag];
    }
}
